/* eslint-disable complexity */
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Banner, TabLayout } from 'src/@vymo/ui/blocks';
import SkeletonLoader from 'src/@vymo/ui/blocks/skelton';
import { Widget } from 'src/@vymo/ui/molecules';
import classnames from 'classnames';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import TileActionHandler from './actions/tileActionHandler';
import { getItemCode } from './queries';
import {
  getSelectedTileData,
  getSpgWidgetDataForCode,
  getWidgetData,
} from './selector';
import { fetchTileAction } from './thunk';
import TileViewRenderer from './tile/tileView';
import { SelectedTileData } from './types';
import styles from './index.module.scss';

function SPGWidget({ widgetCode }) {
  const dispatch = useAppDispatch();
  const { isLoading, isTileActionLoading, tileActionError } = useAppSelector(
    (state) => getWidgetData(state, widgetCode),
  ) ?? {
    isLoading: false,
    isTileActionLoading: false,
    tileActionError: '',
  };

  const widgetDataForCode = useAppSelector((state) =>
    getSpgWidgetDataForCode(state, widgetCode),
  );

  const selectedTileData = useAppSelector((state) =>
    getSelectedTileData(state, widgetCode),
  ) as SelectedTileData;

  const [tileIndexClicked, setTileIndexClicked] = useState(-1);
  const categories = widgetDataForCode?.data?.categories ?? [];
  const allItems = widgetDataForCode?.data?.items ?? [];
  const shouldShowTabs = categories.length > 0 && allItems.length > 0;

  useEffect(() => {
    if (!isTileActionLoading) setTileIndexClicked(-1);
  }, [isTileActionLoading]);

  const onClickTileHandler = (tile, tileIndex) => {
    setTileIndexClicked(tileIndex);
    dispatch(fetchTileAction({ widgetCode, tile }));
  };

  const tabItems = categories.map((category, index) => {
    const categoryItemCodes = (category?.items ?? [])
      .map((item) => (typeof item === 'string' ? item : getItemCode(item)))
      .filter(Boolean);

    const categoryFilteredItems = allItems.filter((item) =>
      categoryItemCodes.includes(getItemCode(item)),
    );

    const filteredItems =
      categoryItemCodes.length > 0 ? categoryFilteredItems : allItems;

    return {
      key: category?.categoryName
        ? `${category.categoryName}-${index}`
        : `category-${index}`,
      label: category?.categoryName || `Category ${index + 1}`,
      children: (
        <div
          className={classnames(styles.spg__tiles__container, 'spg__tiles__jc')}
        >
          {filteredItems.map((item, tileIndex) => (
            <TileViewRenderer
              key={getItemCode(item) || `${tileIndex}`}
              viewType={widgetDataForCode?.data?.viewType}
              index={tileIndex}
              isLoading={tileIndex === tileIndexClicked}
              data={item}
              onClick={onClickTileHandler}
            />
          ))}
        </div>
      ),
    };
  });

  if (allItems.length === 0) {
    return null;
  }

  if (isLoading) {
    return <SkeletonLoader lines={4} />;
  }

  return (
    <>
      {_.isEmpty(selectedTileData) && tileActionError && (
        <Banner
          position="topRight"
          closeable
          message={tileActionError ?? ''}
          variant="error"
          duration={3000}
        />
      )}
      <Widget
        title={widgetDataForCode?.data?.name || 'SPG Widget'}
        content={
          <>
            {shouldShowTabs ? (
              <TabLayout items={tabItems} defaultKey={tabItems[0]?.key} />
            ) : (
              <div
                className={classnames(
                  styles.spg__tiles__container,
                  'spg__tiles__jc',
                )}
              >
                {allItems.map((item, tileIndex) => (
                  <TileViewRenderer
                    key={getItemCode(item) || `${tileIndex}`}
                    viewType={widgetDataForCode?.data?.viewType}
                    index={tileIndex}
                    isLoading={tileIndex === tileIndexClicked}
                    data={item}
                    onClick={onClickTileHandler}
                  />
                ))}
              </div>
            )}
            {!_.isEmpty(selectedTileData) && (
              <TileActionHandler
                action={selectedTileData?.data?.action}
                error={tileActionError}
                widgetCode={widgetCode}
              />
            )}
          </>
        }
      />
    </>
  );
}

export default SPGWidget;
